import { useState, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import emailjs from "@emailjs/browser";
import SEOHead from "@/components/SEOHead";

const EMAILJS_SERVICE_ID = "service_um7u5bd";
const EMAILJS_QUOTE_TEMPLATE = "template_47g1kl9";
const EMAILJS_SERVICE_TEMPLATE = "template_mcwkrwd";
const EMAILJS_PUBLIC_KEY = "zWbcVrtnB5ZkDEHjo";

type FormType = "quote" | "service";

export default function Contact() {
  const { t } = useLanguage();
  const [activeForm, setActiveForm] = useState<FormType>("quote");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent, formRef: React.RefObject<HTMLFormElement>) => {
    e.preventDefault();
    if (!formRef.current || sending) return;

    setSending(true);
    const templateId = activeForm === "quote" ? EMAILJS_QUOTE_TEMPLATE : EMAILJS_SERVICE_TEMPLATE;

    try {
      await emailjs.sendForm(EMAILJS_SERVICE_ID, templateId, formRef.current, EMAILJS_PUBLIC_KEY);
      setSubmitted(true);
      toast.success(t("contact.successMessage"));
      setTimeout(() => setSubmitted(false), 3000);
    } catch (error) {
      console.error("EmailJS error:", error);
      toast.error(t("contact.errorMessage"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="py-16 lg:py-24">
      <SEOHead
        title="Contact"
        description="Contactați WITTMANN ROMANIA pentru mașini de injecție, roboți industriali, termoregulatoare și uscătoare plastic. Sediu în București și Oradea."
        path="/contact"
        keywords="contact WITTMANN Romania, masini de injectie, roboti industriali, mase plastice, service plastic"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "WITTMANN ROMANIA",
          telephone: "+40720227255",
          email: "comercial@wittmann-group.ro",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Bulevardul Iuliu Maniu 7, corp C",
            addressLocality: "București",
            postalCode: "061102",
            addressCountry: "RO",
          },
        }}
      />
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl lg:text-5xl font-bold text-foreground mb-3">{t("contact.title")}</h1>
          <p className="text-lg text-muted-foreground mb-12">{t("contact.subtitle")}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-6"
          >
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-semibold text-foreground mb-4">{t("contact.address")}</h3>
              <div className="space-y-4 text-sm text-muted-foreground">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">Sediu Central</p>
                    <p>Bulevardul Iuliu Maniu 7, corp C</p>
                    <p>061102 București, România</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">Punct de Lucru</p>
                    <p>Strada Zorelelor 12</p>
                    <p>410583 Oradea, România</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-semibold text-foreground mb-4">{t("contact.phone")}</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Bogdan Nestor — Manager general</p>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-primary" />
                    <a href="tel:+40720227255" className="text-muted-foreground hover:text-foreground transition-colors">+4 0720 227 255</a>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Eduard Lazea — Manager vânzări</p>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-primary" />
                    <a href="tel:+40725227255" className="text-muted-foreground hover:text-foreground transition-colors">+4 0725 227 255</a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-semibold text-foreground mb-4">{t("contact.email")}</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">{t("footer.emailSales")}</p>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-primary" />
                    <a href="mailto:comercial@wittmann-group.ro" className="text-muted-foreground hover:text-foreground transition-colors">comercial@wittmann-group.ro</a>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">{t("footer.emailService")}</p>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-primary" />
                    <a href="mailto:service@wittmann-group.ro" className="text-muted-foreground hover:text-foreground transition-colors">service@wittmann-group.ro</a>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">{t("footer.emailEoat")}</p>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-primary" />
                    <a href="mailto:eoat@wittmann-group.ro" className="text-muted-foreground hover:text-foreground transition-colors">eoat@wittmann-group.ro</a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Forms */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2"
          >
            {/* Form Tabs */}
            <div className="flex border-b border-border mb-8">
              <button
                onClick={() => { setActiveForm("quote"); setSubmitted(false); }}
                className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  activeForm === "quote"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t("contact.formQuote")}
              </button>
              <button
                onClick={() => { setActiveForm("service"); setSubmitted(false); }}
                className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  activeForm === "service"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t("contact.formService")}
              </button>
            </div>

            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <CheckCircle className="w-16 h-16 text-primary mb-4" />
                <p className="text-lg font-semibold text-foreground">{t("contact.successMessage")}</p>
              </div>
            ) : activeForm === "quote" ? (
              <QuoteForm t={t} onSubmit={handleSubmit} sending={sending} />
            ) : (
              <ServiceForm t={t} onSubmit={handleSubmit} sending={sending} />
            )}
          </motion.div>
        </div>

        {/* Google Maps */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 space-y-6"
        >
          <h2 className="text-2xl font-bold text-foreground">{t("contact.mapTitle")}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-foreground mb-3">Sediu Central — București</h3>
              <div className="rounded-xl overflow-hidden border border-border aspect-video">
                <iframe
                  src="https://maps.google.com/maps?q=Wittmann+Battenfeld+Romania,+Bulevardul+Iuliu+Maniu+7,+Bucuresti&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sediu Central București"
                />
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-3">Punct de Lucru — Oradea</h3>
              <div className="rounded-xl overflow-hidden border border-border aspect-video">
                <iframe
                  src="https://maps.google.com/maps?q=Strada+Zorelelor+12,+410583+Oradea,+Romania&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Punct de Lucru Oradea"
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function InputField({ label, name, type = "text", required = true }: { label: string; name: string; type?: string; required?: boolean }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
      <input
        name={name}
        type={type}
        required={required}
        className="w-full px-4 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
      />
    </div>
  );
}

function TextAreaField({ label, name, rows = 4 }: { label: string; name: string; rows?: number }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
      <textarea
        name={name}
        rows={rows}
        required
        className="w-full px-4 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors resize-none"
      />
    </div>
  );
}

function SelectField({ label, name, options }: { label: string; name: string; options: string[] }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
      <select
        name={name}
        required
        className="w-full px-4 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
      >
        <option value="">—</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

function QuoteForm({ t, onSubmit, sending }: { t: (key: string) => string; onSubmit: (e: React.FormEvent, ref: React.RefObject<HTMLFormElement>) => void; sending: boolean }) {
  const formRef = useRef<HTMLFormElement>(null);
  return (
    <form ref={formRef} onSubmit={(e) => onSubmit(e, formRef)} className="space-y-5" autoComplete="off">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <InputField label={t("contact.name")} name="name" />
        <InputField label={t("contact.company")} name="company" />
        <InputField label={t("contact.emailField")} name="email" type="email" />
        <InputField label={t("contact.phoneField")} name="phone" type="tel" />
      </div>
      <SelectField
        label={t("contact.equipmentType")}
        name="equipment_type"
        options={[t("nav.injectionMachines"), t("nav.robots"), t("nav.auxiliaries"), t("nav.centralSystems")]}
      />
      <TextAreaField label={t("contact.message")} name="message" />
      <button
        type="submit"
        disabled={sending}
        className="px-8 py-3 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 transition-colors text-sm disabled:opacity-50"
      >
        {sending ? t("contact.sending") : t("contact.submit")}
      </button>
    </form>
  );
}

function ServiceForm({ t, onSubmit, sending }: { t: (key: string) => string; onSubmit: (e: React.FormEvent, ref: React.RefObject<HTMLFormElement>) => void; sending: boolean }) {
  const formRef = useRef<HTMLFormElement>(null);
  return (
    <form ref={formRef} onSubmit={(e) => onSubmit(e, formRef)} className="space-y-5" autoComplete="off">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <InputField label={t("contact.name")} name="name" />
        <InputField label={t("contact.company")} name="company" />
        <InputField label={t("contact.emailField")} name="email" type="email" />
        <InputField label={t("contact.phoneField")} name="phone" type="tel" />
      </div>
      <SelectField
        label={t("contact.equipmentType")}
        name="equipment_type"
        options={[t("nav.injectionMachines"), t("nav.robots"), t("nav.auxiliaries"), t("nav.centralSystems")]}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <InputField label={t("contact.equipmentSerial")} name="equipment_serial" />
        <SelectField
          label={t("contact.urgency")}
          name="urgency"
          options={[t("contact.urgencyLow"), t("contact.urgencyMedium"), t("contact.urgencyHigh")]}
        />
      </div>
      <TextAreaField label={t("contact.problemDesc")} name="problem_desc" rows={5} />
      <button
        type="submit"
        disabled={sending}
        className="px-8 py-3 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 transition-colors text-sm disabled:opacity-50"
      >
        {sending ? t("contact.sending") : t("contact.submit")}
      </button>
    </form>
  );
}
